// src/pages/AppVersionCustomerPage.js
import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig'; 
import { Save, Info, Smartphone, AlertTriangle } from 'lucide-react';

export default function AppVersionCustomerPage() {
  const [latestVersion, setLatestVersion] = useState('');
  const [minVersion, setMinVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load data dari Firestore khusus untuk Customer
  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    setLoading(true);
    try {
      // Fokus pada document ID 'customer_version'
      const docRef = doc(db, 'app_settings', 'customer_version');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setLatestVersion(data.latest_version || '');
        setMinVersion(data.min_version || '');
      }
    } catch (error) {
      console.error('Error fetching customer versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!latestVersion || !minVersion) {
      return alert('Mohon isi kedua kolom versi!');
    }

    setSaving(true);
    try {
      // Menyimpan ke document ID 'customer_version'
      const docRef = doc(db, 'app_settings', 'customer_version');
      await setDoc(
        docRef,
        {
          latest_version: latestVersion,
          min_version: minVersion,
          updatedAt: new Date(),
        },
        { merge: true }
      );
      alert('✅ Berhasil memperbarui versi Zimo App (Customer)!');
    } catch (error) {
      console.error('Error saving versions:', error);
      alert('❌ Gagal menyimpan data: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-400 font-bold">Memuat pengaturan versi Zimo App...</div>;
  }

  return (
    <div className="p-6 text-gray-100 max-w-3xl">
      <h2 className="text-3xl font-extrabold mb-6 flex items-center gap-2 text-teal-400">
        <Smartphone size={32} />
        Versi Zimo App (Customer)
      </h2>

      {/* Info Panel */}
      <div className="bg-teal-900/20 border border-teal-800 p-4 rounded-xl mb-6 shadow-sm">
        <h3 className="flex items-center gap-2 text-teal-400 font-bold text-lg mb-2">
          <Info size={20} /> Panduan Update Customer
        </h3>
        <ul className="list-disc list-inside text-sm text-gray-300 space-y-2 ml-1">
          <li>
            <strong className="text-white">Versi Terbaru:</strong> Memunculkan notifikasi <i>opsional</i> (bisa dilewati) kepada customer.
          </li>
          <li>
            <strong className="text-white">Versi Minimal:</strong> Memunculkan notifikasi <i>wajib</i> (Force Update). Customer <b>tidak bisa menggunakan aplikasi</b> sampai melakukan update.
          </li>
        </ul>
      </div>

      {/* Form Input */}
      <form onSubmit={handleSave} className="bg-gray-900 border border-gray-800 p-6 rounded-xl shadow-xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          
          {/* Input Versi Terbaru */}
          <div>
            <label className="flex text-gray-300 font-bold mb-2 items-center gap-2">
              Versi Terbaru (Latest Version)
            </label>
            <input
              type="text"
              placeholder="Contoh: 2.0.5"
              value={latestVersion}
              onChange={(e) => setLatestVersion(e.target.value)}
              className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white font-bold focus:outline-none focus:border-teal-500"
            />
          </div>

          {/* Input Versi Minimal */}
          <div>
            <label className="flex text-orange-400 font-bold mb-2 items-center gap-2">
              <AlertTriangle size={18} />
              Versi Minimal (Force Update)
            </label>
            <input
              type="text"
              placeholder="Contoh: 2.0.0"
              value={minVersion}
              onChange={(e) => setMinVersion(e.target.value)}
              className="w-full p-3 bg-gray-800 border border-orange-900/50 rounded-lg text-white font-bold focus:outline-none focus:border-orange-500"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="flex items-center justify-center gap-2 w-full md:w-auto px-6 py-3 bg-gradient-to-r from-orange-500 to-teal-600 hover:from-orange-600 hover:to-teal-700 text-white font-extrabold rounded-lg transition-all shadow-md disabled:opacity-50"
        >
          <Save size={20} />
          {saving ? 'Menyimpan...' : 'Simpan Pengaturan Zimo App'}
        </button>
      </form>
    </div>
  );
}